import { PricingCalculatorContextValue } from "./context.tsx";
import { BreakdownPriceTier, FormattedBreakdownPriceTier } from "./types.ts";

export const getUnitCount = (ctx: PricingCalculatorContextValue) =>
    ctx.inputValues.unitCount;
export const getBillingCycle = (ctx: PricingCalculatorContextValue) =>
    ctx.inputValues.billingCycle;

export const hasAnnualBilling = (ctx: PricingCalculatorContextValue) => {
    return ctx.config.billingCycleConfig.annual;
};
export const hasMonthlyBilling = (ctx: PricingCalculatorContextValue) => {
    return ctx.config.billingCycleConfig.monthly;
};

export const isUnitCountAboveMaxThreshold = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const unitCount = getUnitCount(ctx);
    const planLimitConfig = getPlanLimitConfig(ctx, offeringSku);
    return unitCount > planLimitConfig.signupLimit;
};

export const getUnitCountMinThreshold = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const { minUnitCount } = getPlanLimitConfig(ctx, offeringSku);
    return minUnitCount ?? 0;
};

export const isUnitCountBelowMinThreshold = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const minUnitCount = getUnitCountMinThreshold(ctx, offeringSku);
    const unitCount = getUnitCount(ctx);
    return unitCount < minUnitCount;
};

/**
 * @deprecated this should be replaced by isUnitCountBelowMinThreshold
 */
export const isUnitCountAboveEnterpriseMinThreshold = (
    ctx: PricingCalculatorContextValue
) => {
    const unitCount = getUnitCount(ctx);
    const unitMinEnterprise = ctx.config.unitMinEnterprise;
    return unitCount >= unitMinEnterprise;
};

const getPlanLimitConfigs = (ctx: PricingCalculatorContextValue) => {
    return ctx.config.planLimitConfigs;
};

const getPlanLimitConfig = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const planLimitConfigs = getPlanLimitConfigs(ctx);
    const planLimitConfig = planLimitConfigs.find(
        (plan) => plan.offeringSku === offeringSku
    );
    if (!planLimitConfig) {
        throw new Error("unexpected empty value");
    }
    return planLimitConfig;
};

const getPlanPriceResult = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const planPriceResult = ctx.calculationResult.find(
        (plan) => plan.offeringSku === offeringSku
    );
    if (!planPriceResult) {
        throw new Error("unexpected empty value");
    }
    return planPriceResult;
};

export const getPlanTotalPriceFormatted = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const price = getPlanPriceResult(ctx, offeringSku);
    let { totalPrice } = price;

    const { getDiscountedPrice } = ctx;
    if (getDiscountedPrice) {
        totalPrice = getDiscountedPrice({
            price: totalPrice,
            inputs: ctx.inputValues
        });
    }

    return formatPrice(ctx, totalPrice);
};

export const getPlanAvgPerUnitPrice = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
): number => {
    const price = getPlanPriceResult(ctx, offeringSku);
    return price.avgPerUnitPrice;
};

export const roundPrice = (price: number): number => {
    return Math.round(price);
};

export const roundJPYPrice = (price: number): number => {
    return Math.ceil(price);
};

export const getNumberFormatLocale = (ctx: PricingCalculatorContextValue) => {
    const currencyISOCode = getCurrencyIsoCode(ctx);
    const isJPY = currencyISOCode === "JPY";
    return isJPY ? "ja-JP" : "en-US";
};

export const formatPrice = (
    ctx: PricingCalculatorContextValue,
    price: number,
    options: { minimumFractionDigits?: number } = {}
): string => {
    const numberFormatLocale = getNumberFormatLocale(ctx);
    const currencyISOCode = getCurrencyIsoCode(ctx);
    return new Intl.NumberFormat(numberFormatLocale, {
        style: "currency",
        currency: currencyISOCode,
        minimumFractionDigits: options.minimumFractionDigits ?? 0,
        maximumFractionDigits: options.minimumFractionDigits ?? 0
    }).format(price);
};

export const getPlanAvgPerUnitPriceFormatted = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const price = getPlanPriceResult(ctx, offeringSku);
    return price.formattedAvgPerUnitPrice;
};

export const isOfferingRecommended = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const recommendedOfferingSku = getRecommendedOfferingSku(ctx);
    return recommendedOfferingSku === offeringSku;
};

const getRecommendedOfferingSku = (ctx: PricingCalculatorContextValue) => {
    const getRecommendedPlanIdx = (
        unitCount: number,
        minUnitCounts: number[]
    ) => {
        const len = minUnitCounts.length - 1;
        return (
            len -
            minUnitCounts
                .reverse()
                .findIndex((minUnitCount) => unitCount > minUnitCount)
        );
    };

    const unitCount = getUnitCount(ctx);
    const planLimitConfigs = getPlanLimitConfigs(ctx);
    const recommendedPlanIdx = getRecommendedPlanIdx(
        unitCount,
        planLimitConfigs.map((productPlan) => productPlan.recommendationLimit)
    );
    return planLimitConfigs[recommendedPlanIdx]?.offeringSku;
};

const getUnformattedBreakdown = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
): BreakdownPriceTier | undefined => {
    const planPriceResult = getPlanPriceResult(ctx, offeringSku);
    return planPriceResult.breakdown.at(-1);
};
export const getUnitTierLowerBound = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const breakdown = getUnformattedBreakdown(ctx, offeringSku);
    return breakdown?.unitLimitLower;
};
export const getUnitTierUpperBound = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const breakdown = getUnformattedBreakdown(ctx, offeringSku);
    return breakdown?.unitLimitUpper;
};

export const getUnitsAvailableInTier = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const unitCount = getUnitCount(ctx);
    const unitTierUpperBound = getUnitTierUpperBound(ctx, offeringSku);
    return (unitTierUpperBound ?? 0) - unitCount;
};

/**
 * @deprecated
 */
const getFormattedBreakdown = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
): FormattedBreakdownPriceTier | undefined => {
    const planPriceResult = getPlanPriceResult(ctx, offeringSku);
    return planPriceResult.formattedBreakdown.at(-1);
};

/**
 * @deprecated
 */
export const getUnitTierLowerBoundFormatted = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const breakdown = getFormattedBreakdown(ctx, offeringSku);
    return breakdown?.formattedUnitLimitLower;
};

/**
 * @deprecated
 */
export const getUnitTierUpperBoundFormatted = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
) => {
    const breakdown = getFormattedBreakdown(ctx, offeringSku);
    return breakdown?.formattedUnitLimitUpper;
};

export const getCurrencyIsoCode = (ctx: PricingCalculatorContextValue) =>
    ctx.config.currencyISOCode;

export const getFormattedPlanPrice = (
    ctx: PricingCalculatorContextValue,
    offeringSku: string
): string => {
    const billingCycle = getBillingCycle(ctx);
    const currencyISOCode = getCurrencyIsoCode(ctx);

    if (billingCycle === "ANNUAL") {
        return getPlanTotalPriceFormatted(ctx, offeringSku);
    }

    let price = getPlanAvgPerUnitPrice(ctx, offeringSku);
    if (currencyISOCode === "JPY") {
        price = roundJPYPrice(price);
        return formatPrice(ctx, price, {
            minimumFractionDigits: 0
        });
    }

    return formatPrice(ctx, price, {
        minimumFractionDigits: price === 0 || Number.isInteger(price) ? 0 : 2
    });
};

export interface GetPricePlanIdArgs {
    offeringSku: string;
    edition: string;
}
export const getPricePlanId = (
    ctx: PricingCalculatorContextValue,
    args: GetPricePlanIdArgs
) => {
    const { offeringSku, edition } = args;

    const offering = Object.values(ctx.config.ccpPricingData).find(
        (offering) => offering.sku === offeringSku
    );

    const currencyISOCode = getCurrencyIsoCode(ctx);
    const billingCycle = getBillingCycle(ctx);

    const defaultPricingPlan = offering?.defaultPricingPlans?.filter(
        (item) =>
            item.currency.toLowerCase() == currencyISOCode.toLowerCase() &&
            item.offeringName.toLowerCase() == edition.toLowerCase() &&
            item.primaryCycle_name.toLowerCase() ==
                billingCycle.toLowerCase() &&
            item.status.toLowerCase() == "active"
    );

    const lastPricingPlan = defaultPricingPlan?.[defaultPricingPlan.length - 1];

    return lastPricingPlan?.id || "";
};
